/**
 * PULSE Peekaboo Controller
 * ------------------------------------------------------------
 * A restrained, timing-aware purchase reminder. It lives outside
 * #MainContent (see theme.liquid) so its state survives every
 * shell screen transition untouched.
 *
 * Rules encoded here:
 *  - Says nothing for an initial "let them explore" window.
 *  - Only offers to peek during a scroll-idle moment, never mid-scroll.
 *  - Auto-retreats if ignored; re-offers only after a long, randomized gap.
 *  - An explicit close (tab or panel) enforces a hard ~60s suppression.
 *  - Never shows at all on the product page itself — redundant there.
 */
(function(){
  var root = document.getElementById('PulsePeekaboo');
  if(!root) return;

  var tab = document.getElementById('PeekabooTab');
  var tabClose = document.getElementById('PeekabooClose');
  var panel = document.getElementById('PeekabooPanel');
  var panelClose = document.getElementById('PeekabooPanelClose');
  var productUrl = root.getAttribute('data-product-url') || '';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var STORE_KEY = 'pulsePeekaboo';
  var HIDE_MS = 7000;                 // how long an ignored peek stays out before retreating
  var COOLDOWN_MS = 60000;            // explicit-close suppression window
  var RECHECK_MS = 50000;             // base gap between un-forced re-peeks
  var RECHECK_JITTER_MS = 30000;      // + random jitter, so it never feels mechanical
  var MIN_TIME_ON_PAGE_MS = 26000;    // let them explore before the very first peek
  var MIN_TIME_ON_PAGE_NO_SCROLL_MS = 48000; // fallback if they simply haven't scrolled
  var MIN_SCROLL_PX = 280;
  var SCROLL_IDLE_MS = 700;           // how long scrolling must have stopped before we consider peeking
  var SCROLL_FAST_PX_PER_S = 900;     // above this, treat as "actively scrolling", never peek

  var pageLoadAt = Date.now();
  var suppressedUntil = 0;
  var nextEligibleAt = pageLoadAt + MIN_TIME_ON_PAGE_MS;
  var scrollIdleTimer = null;
  var lastScrollY = window.scrollY;
  var lastScrollAt = Date.now();
  var hideTimer = null;

  // Persist cooldown/timing across full reloads (not just shell transitions).
  try{
    var saved = JSON.parse(sessionStorage.getItem(STORE_KEY) || '{}');
    if(saved.suppressedUntil) suppressedUntil = saved.suppressedUntil;
    if(saved.nextEligibleAt) nextEligibleAt = Math.max(nextEligibleAt, saved.nextEligibleAt);
  }catch(e){}

  function persist(){
    try{
      sessionStorage.setItem(STORE_KEY, JSON.stringify({
        suppressedUntil: suppressedUntil,
        nextEligibleAt: nextEligibleAt
      }));
    }catch(e){}
  }

  function onProductPage(){
    if(!productUrl) return false;
    try{
      var productPath = new URL(productUrl, window.location.origin).pathname;
      return window.location.pathname === productPath;
    }catch(e){ return false; }
  }

  function setState(state){
    root.setAttribute('data-state', state);
    tab.setAttribute('aria-expanded', state === 'expanded' ? 'true' : 'false');
  }

  function clearHideTimer(){
    if(hideTimer){ clearTimeout(hideTimer); hideTimer = null; }
  }

  function retreat(){
    clearHideTimer();
    setState('collapsed');
  }

  function scheduleNextEligibility(){
    var jitter = Math.random() * RECHECK_JITTER_MS;
    nextEligibleAt = Date.now() + RECHECK_MS + jitter;
    persist();
  }

  function peek(){
    if(onProductPage()) return;
    setState('peeking');
    clearHideTimer();
    hideTimer = setTimeout(function(){
      // Only auto-retreat if the visitor never engaged with it.
      if(root.getAttribute('data-state') === 'peeking') retreat();
    }, HIDE_MS);
    scheduleNextEligibility();
  }

  function expand(){
    clearHideTimer();
    setState('expanded');
    // Move focus into the panel for keyboard/screen-reader users.
    var heading = document.getElementById('PeekabooPanelHeading');
    if(heading){ heading.setAttribute('tabindex', '-1'); heading.focus({ preventScroll:true }); }
  }

  function dismiss(withCooldown){
    clearHideTimer();
    setState('collapsed');
    if(withCooldown){
      suppressedUntil = Date.now() + COOLDOWN_MS;
      nextEligibleAt = Math.max(nextEligibleAt, suppressedUntil);
      persist();
    }
  }

  function attemptPeek(){
    if(onProductPage()) return;
    if(reduceMotion) return; // a static, restrained reminder isn't worth the motion for these visitors
    var state = root.getAttribute('data-state');
    if(state !== 'collapsed') return;

    var now = Date.now();
    if(now < suppressedUntil) return;
    if(now < nextEligibleAt) return;

    var timeOnPage = now - pageLoadAt;
    var scrolled = window.scrollY > MIN_SCROLL_PX;
    var enoughTime = scrolled ? timeOnPage > MIN_TIME_ON_PAGE_MS : timeOnPage > MIN_TIME_ON_PAGE_NO_SCROLL_MS;
    if(!enoughTime) return;

    peek();
  }

  // ---- Scroll handling: never peek mid-scroll, only on a genuine pause ----
  window.addEventListener('scroll', function(){
    var now = Date.now();
    var elapsed = now - lastScrollAt;
    if(elapsed > 0){
      var speed = Math.abs(window.scrollY - lastScrollY) / (elapsed / 1000);
      root.dataset.fastScroll = speed > SCROLL_FAST_PX_PER_S ? '1' : '0';
    }
    lastScrollY = window.scrollY;
    lastScrollAt = now;

    if(scrollIdleTimer) clearTimeout(scrollIdleTimer);
    scrollIdleTimer = setTimeout(function(){
      if(root.dataset.fastScroll === '1') return;
      attemptPeek();
    }, SCROLL_IDLE_MS);
  }, { passive:true });

  // A gentle background check too, so a visitor who lands and never scrolls
  // (short page, already at the bottom, etc.) can still be offered a peek.
  var heartbeat = setInterval(attemptPeek, 8000);

  // ---- Interactions ----
  tab.addEventListener('click', function(e){
    if(e.target === tabClose) return;
    expand();
  });
  tab.addEventListener('keydown', function(e){
    if(e.key === 'Enter' || e.key === ' '){ e.preventDefault(); expand(); }
  });

  tabClose.addEventListener('click', function(e){
    e.stopPropagation();
    dismiss(true);
  });

  panelClose.addEventListener('click', function(e){
    e.stopPropagation();
    dismiss(true);
  });

  // Click outside the expanded panel collapses it (without the hard cooldown —
  // that's reserved for an explicit close action).
  document.addEventListener('click', function(e){
    if(root.getAttribute('data-state') !== 'expanded') return;
    if(root.contains(e.target)) return;
    setState('collapsed');
  });

  document.addEventListener('keydown', function(e){
    if(e.key === 'Escape' && root.getAttribute('data-state') === 'expanded'){
      setState('collapsed');
      tab.focus();
    }
  });

  // Re-check visibility after every shell screen transition (e.g. landing
  // on the product page should hide it immediately; leaving it should not
  // force an unwanted peek).
  window.addEventListener('pulse:navigated', function(){
    if(onProductPage() && root.getAttribute('data-state') !== 'collapsed'){
      retreat();
    }
  });

  if(onProductPage()) root.setAttribute('hidden', '');
  window.addEventListener('pulse:navigated', function(){
    if(onProductPage()) root.setAttribute('hidden', '');
    else root.removeAttribute('hidden');
  });
})();
